import type { ThreeEvent } from "@react-three/fiber";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Group } from "three";
import { useDragStore } from "../stores/dragStore";
import type { AbstractProductDefinition } from "./AbstractProduct";
import { HardwareInstancingProvider } from "./HardwareInstancingProvider";
import { useProductStoreContext } from "./ProductStoreContext";
import { resolvePartRaycast } from "./partRaycast";

interface GroupAnimationState {
  isOpen: boolean;
  isAnimating: boolean;
}

function hasAnimationMethods(product: unknown): product is {
  getAvailableAnimationGroupNames(): string[];
  getGroupAnimationState(groupName: string): GroupAnimationState | undefined;
  registerAnimationGroup(groupName: string, groupRef: Group): boolean;
  isGroupRegistered(groupName: string): boolean;
  toggleAnimationGroup(groupName: string): boolean;
  isGroupInteractive(groupName: string): boolean;
  getAllGroupAnimationStates(): Map<string, GroupAnimationState>;
} {
  return (
    product != null &&
    typeof product === "object" &&
    "getAvailableAnimationGroupNames" in product &&
    "registerAnimationGroup" in product &&
    "toggleAnimationGroup" in product &&
    typeof product.getAvailableAnimationGroupNames === "function" &&
    typeof product.registerAnimationGroup === "function" &&
    typeof product.toggleAnimationGroup === "function"
  );
}

export const useProductViewerGroupInteractions = (
  product: AbstractProductDefinition | null,
) => {
  const useProductStore = useProductStoreContext();
  const [groupStates, setGroupStates] = useState<
    Record<string, GroupAnimationState>
  >({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: this should only run if blueprint or partDefinitions change
  useEffect(() => {
    if (!product || !hasAnimationMethods(product)) {
      return;
    }

    const availableGroupNames = product.getAvailableAnimationGroupNames();
    const newGroupStates: Record<string, GroupAnimationState> = {};

    availableGroupNames.forEach((groupName: string) => {
      const state = product.getGroupAnimationState(groupName);
      if (state) {
        newGroupStates[groupName] = {
          isOpen: state.isOpen,
          isAnimating: state.isAnimating,
        };
      }
    });

    setGroupStates(newGroupStates);
  }, [product?.blueprint, product?.partDefinitions]);

  const registerGroup = useCallback(
    (groupName: string, groupRef: Group) => {
      if (!product || !hasAnimationMethods(product)) {
        return;
      }

      // No early-out on `isGroupRegistered`: when the part tree remounts, this
      // group name may arrive with a different Group object, and only the
      // manager can tell whether it is the one it already holds.
      product.registerAnimationGroup(groupName, groupRef);
    },
    [product],
  );

  const toggleGroup = useCallback(
    (groupName: string) => {
      if (!product || !hasAnimationMethods(product)) {
        return;
      }

      if (!product.isGroupInteractive(groupName)) {
        return;
      }

      product.toggleAnimationGroup(groupName);
      useProductStore.getState().bumpLidsVersion();

      setGroupStates(() => {
        const allStates = product.getAllGroupAnimationStates();
        const newStates: Record<string, GroupAnimationState> = {};

        allStates.forEach((state: GroupAnimationState, groupName: string) => {
          newStates[groupName] = {
            isOpen: state.isOpen,
            isAnimating: state.isAnimating,
          };
        });

        return newStates;
      });
    },
    [product, useProductStore],
  );

  return { groupStates, toggleGroup, registerGroup };
};

/**
 * Whether the lid group around the reader is hidden. The group itself is only
 * `visible={false}`, which three's raycaster ignores, so its parts read this
 * to opt out of hit testing and drag affordances like a hidden part type.
 */
const LidGroupHiddenContext = createContext(false);

export const useIsLidGroupHidden = (): boolean =>
  useContext(LidGroupHiddenContext);

/** `userData` of a hidden lid group: the camera fit skips it through `VISIBLE_PRODUCT_BOUNDS`; floor alignment does not. */
const HIDDEN_LID_GROUP_USER_DATA = { isHiddenLidGroup: true } as const;
const EMPTY_USER_DATA = {} as const;

export const InteractiveGroup = ({
  groupName,
  children,
  groupState,
  onToggle,
  onRegister,
}: {
  groupName: string;
  children: React.ReactNode;
  groupState?: GroupAnimationState;
  onToggle: (groupName: string) => void;
  onRegister: (groupName: string, groupRef: Group) => void;
}) => {
  const groupRef = useRef<Group>(null);
  const isDragActive = useDragStore((state) => state.isDragActive);
  const useProductStore = useProductStoreContext();
  // Read here and nowhere else: the group stays mounted and is only hidden, so
  // its parts keep their meshes and a toggle does not rebuild anything.
  const isHidden = useProductStore(
    (state) => !state.showLids && state.product?.isLidGroup(groupName) === true,
  );

  useEffect(() => {
    if (groupRef.current && groupState) {
      onRegister(groupName, groupRef.current);
    }
  }, [groupName, groupState, onRegister]);

  const handleClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (isDragActive) {
        event.stopPropagation();
        return;
      }

      if (
        (event.nativeEvent.ctrlKey || event.nativeEvent.metaKey) &&
        event.nativeEvent.button === 0
      ) {
        event.stopPropagation();
        onToggle(groupName);
      }
    },
    [groupName, onToggle, isDragActive],
  );

  return (
    <group
      ref={groupRef}
      visible={!isHidden}
      raycast={resolvePartRaycast(isHidden)}
      userData={isHidden ? HIDDEN_LID_GROUP_USER_DATA : EMPTY_USER_DATA}
      {...(groupState ? { onClick: handleClick } : {})}
    >
      <LidGroupHiddenContext.Provider value={isHidden}>
        <HardwareInstancingProvider animationGroup={groupName}>
          {children}
        </HardwareInstancingProvider>
      </LidGroupHiddenContext.Provider>
    </group>
  );
};
